import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

/**
 * Shift+drag on the map to draw a rectangle and select all stops inside.
 * Props:
 *   stops: [{ id, lat, lng }]
 *   onBoxSelect: (selectedIds: string[], append: boolean) => void
 */
export default function BoxSelect({ stops = [], onBoxSelect }) {
  const map = useMap();
  const stateRef = useRef({ rect: null, startLatLng: null, active: false });
  const stopsRef = useRef(stops);
  useEffect(() => { stopsRef.current = stops; }, [stops]);

  useEffect(() => {
    if (!map) return;
    const container = map.getContainer();

    const onKeyDown = (e) => {
      if (e.key === "Shift") { container.style.cursor = "crosshair"; }
    };
    const onKeyUp = (e) => {
      if (e.key === "Shift") { container.style.cursor = ""; }
    };

    const finish = (latlng, append) => {
      if (!stateRef.current.active) return;
      stateRef.current.active = false;
      map.dragging.enable();
      container.style.cursor = "";
      const rect = stateRef.current.rect;
      let bounds = null;
      if (rect) {
        bounds = latlng ? L.latLngBounds(stateRef.current.startLatLng, latlng) : rect.getBounds();
        map.removeLayer(rect);
        stateRef.current.rect = null;
      }
      if (!bounds) return;
      const ids = [];
      for (const s of stopsRef.current) {
        if (s.lat == null || s.lng == null) continue;
        if (bounds.contains(L.latLng(s.lat, s.lng))) ids.push(s.id);
      }
      if (onBoxSelect) onBoxSelect(ids, append);
    };

    const onMouseDown = (e) => {
      if (!e.originalEvent.shiftKey) return;
      map.dragging.disable();
      stateRef.current.active = true;
      stateRef.current.startLatLng = e.latlng;
      const bounds = L.latLngBounds(e.latlng, e.latlng);
      stateRef.current.rect = L.rectangle(bounds, {
        color: "#10b981", weight: 2, fillOpacity: 0.12, dashArray: "4 4",
      }).addTo(map);
    };
    const onMouseMove = (e) => {
      if (!stateRef.current.active) return;
      const bounds = L.latLngBounds(stateRef.current.startLatLng, e.latlng);
      stateRef.current.rect.setBounds(bounds);
    };
    const onMouseUp = (e) => {
      finish(e.latlng, e.originalEvent?.metaKey || e.originalEvent?.ctrlKey);
    };
    const onWindowMouseUp = (e) => {
      finish(null, e.metaKey || e.ctrlKey);
    };

    map.on("mousedown", onMouseDown);
    map.on("mousemove", onMouseMove);
    map.on("mouseup", onMouseUp);
    window.addEventListener("mouseup", onWindowMouseUp);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      map.off("mousedown", onMouseDown);
      map.off("mousemove", onMouseMove);
      map.off("mouseup", onMouseUp);
      window.removeEventListener("mouseup", onWindowMouseUp);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      container.style.cursor = "";
    };
  }, [map, onBoxSelect]);

  return null;
}
